<script setup>
import Modal from "../../../../../components/ui-components/modal/Modal.vue";
import {ref, defineExpose, onMounted, nextTick, getCurrentInstance, watch} from "vue";
import KSASecondaryButton from "@/components/ui-components/button/KSASecondaryButton.vue";
import KSAPrimaryButton from "@/components/ui-components/button/KSAPrimaryButton.vue";
import {usePageLoadingStore} from "@/stores/pageLoading";
import mypageUserResumeService from "@/pages/user/services/mypage/mypageUserResumeService";
import { useAlertStore } from '../../../../../stores/alertStore';
const { $alert, $confirm } = useAlertStore();

const globals = getCurrentInstance().appContext.config.globalProperties;
const { $pageListBoardNo } = globals;

const baseModal = ref(null);
const resolvePromise = ref(null);
const loadingStore = usePageLoadingStore()

// parent에 선택된 지역 데이터 보내기 위해 emit 선언.
const emit = defineEmits(['submitSelection']);

const selectedResult = ref([]); // 최종 선택된 수료 강의들
const selectedLectures = ref([]); // 선택 강의 상세정보(체크 동기화용)

const list = ref([]);
const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const totalCount = ref(0);
const isSelectedAll= ref(false);
const suppressSelectAllWatch = ref(false);

const setIsSelectedAllSilently = (value) => {
  suppressSelectAllWatch.value = true;
  isSelectedAll.value = value;
  nextTick(() => {
    suppressSelectAllWatch.value = false;
  });
};

onMounted( async ()=>{
  await nextTick();
})

const pageSizeGroup = ref([
  { label: "10개씩 보기", value: "10" },
  { label: "30개씩 보기", value: "30" },
  { label: "50개씩 보기", value: "50" },
  { label: "100개씩 보기", value: "100" },
  { label: "500개씩 보기", value: "500" }
]);

const getInitialSearchInput = () => ({
  lectureGb: '00',
  partnerYn: 'A',
  searchInput: '',
});

const _searchInput = ref(getInitialSearchInput());

const lectureGb = ref([
  { label:"구분 전체",  value: "00" },
  { label:"온라인",  value: "01" },
  { label:"오프라인",  value: "02" },
]);

const partnerYn = ref([
  { label:"담당 구분 전체",  value: "A" },
  { label:"수도권",  value: "N" },
  { label:"비수도권",  value: "Y" },
]);

const normalizeSelectedLectures = (selectedLectureItems = []) => {
  selectedLectures.value = selectedLectureItems.map((item, index) => {
    if (typeof item === "object" && item !== null) {
      return {
        key: item.key || `name:${item.lectName || ""}:${index}`,
        lectName: item.lectName || "",
        lectId: item.lectId ?? null,
      };
    }

    return {
      key: `name:${item}:${index}`,
      lectName: item,
      lectId: null,
    };
  });
  selectedResult.value = selectedLectures.value.map((lecture) => lecture.lectName);
};

const retrieveList = () => {
  let paramObj = {
    lectGb: _searchInput.value.lectureGb,
    partnerYn: _searchInput.value.partnerYn,
    searchInput: _searchInput.value.searchInput,
    pageNumber: currentPage.value,
    size: pageSize.value,
  }

  loadingStore.loading = true;
  mypageUserResumeService.retrieveLectureList(paramObj)
      .then( async (res) => {
        loadingStore.loading = false;
        // console.log(res);
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          const selectedKeys = new Set(selectedLectures.value.map(item => item.key));
          const selectedNames = new Set(selectedLectures.value.map(item => item.lectName));
          const hasNamePlaceholderSelection = selectedLectures.value.some((item) => String(item.key).startsWith("name:"));
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value).map((lecture) => {
            const lectureKey = getLectureKey(lecture);
            return {
              ...lecture,
              checked: selectedKeys.has(lectureKey) || (hasNamePlaceholderSelection && selectedNames.has(lecture.lectName))
            };
          });
          setIsSelectedAllSilently(list.value.length > 0 && list.value.every((lecture) => lecture.checked));

        } else {
        }
      }).catch( (error) =>  {
    // console.log(error);
    loadingStore.loading = false;
  });
}

const onSelectLectureGb = (option) => {
  _searchInput.value.lectureGb = option.value;
}

const onSelectPartnerYn = (option) => {
  _searchInput.value.partnerYn = option.value;
}

const getLectureKey = (lecture) => {
  return lecture.lectId || `${lecture.lectName}|${lecture.lectStartYmd}|${lecture.lectEndYmd}`;
};

const updateSelectedResult = () => {
  selectedResult.value = selectedLectures.value.map((lecture) => lecture.lectName);
};

const addSelectedLecture = (lecture) => {
  const lectureKey = getLectureKey(lecture);
  const exists = selectedLectures.value.some((item) => item.key === lectureKey);
  if (!exists) {
    const placeholderIndex = selectedLectures.value.findIndex(
      (item) => String(item.key).startsWith("name:") && item.lectName === lecture.lectName
    );
    if (placeholderIndex > -1) {
      selectedLectures.value.splice(placeholderIndex, 1);
    }

    selectedLectures.value.push({
      key: lectureKey,
      lectName: lecture.lectName,
      lectId: lecture.lectId ?? null,
    });
  }
};

const removeSelectedLecture = (lecture) => {
  const lectureKey = getLectureKey(lecture);
  selectedLectures.value = selectedLectures.value.filter((item) => item.key !== lectureKey && item.lectName !== lecture.lectName);
};

watch(isSelectedAll, (checked) => {
  if (suppressSelectAllWatch.value) {
    return;
  }

  list.value.forEach((lecture) => {
    lecture.checked = checked;
  });
});

watch(
  () => list.value.map((lecture) => `${getLectureKey(lecture)}:${lecture.checked ? 1 : 0}`),
  () => {
    const selectedMap = new Map(selectedLectures.value.map((item) => [item.key, item]));

    list.value.forEach((lecture) => {
      const lectureKey = getLectureKey(lecture);
      if (lecture.checked) {
        if (!selectedMap.has(lectureKey)) {
          const placeholder = Array.from(selectedMap.values()).find(
            (item) => String(item.key).startsWith("name:") && item.lectName === lecture.lectName
          );
          if (placeholder) {
            selectedMap.delete(placeholder.key);
          }
          selectedMap.set(lectureKey, {
            key: lectureKey,
            lectName: lecture.lectName,
            lectId: lecture.lectId ?? null,
          });
        }
      } else {
        selectedMap.delete(lectureKey);
      }
    });

    selectedLectures.value = Array.from(selectedMap.values());
    updateSelectedResult();

    const allChecked = list.value.length > 0 && list.value.every((lecture) => lecture.checked);
    if (isSelectedAll.value !== allChecked) {
      setIsSelectedAllSilently(allChecked);
    }
  }
);


// 삭제 버튼
const deleteLecture = (index) => {
  const target = selectedLectures.value[index];
  if (!target) {
    return;
  }

  selectedLectures.value = selectedLectures.value.filter((_, itemIndex) => itemIndex !== index);
  list.value.forEach((lecture) => {
    if (getLectureKey(lecture) === target.key) {
      lecture.checked = false;
    }
  });

  // Placeholder key일 때는 동일 강의명으로 체크된 항목 1개를 해제해 UI와 선택목록을 맞춘다.
  if (String(target.key).startsWith("name:")) {
    const checkedLecture = list.value.find((lecture) => lecture.checked && lecture.lectName === target.lectName);
    if (checkedLecture) {
      checkedLecture.checked = false;
    }
  }

  updateSelectedResult();
  setIsSelectedAllSilently(list.value.length > 0 && list.value.every((lecture) => lecture.checked));
};

// 초기화 버튼
const reset = () => {
  selectedLectures.value = [];
  selectedResult.value = [];
  list.value.forEach((lecture) => {
    lecture.checked = false;
  });
  setIsSelectedAllSilently(false);
};

// 검색 버튼
const onClickSearch = () => {
  currentPage.value = 1;
  retrieveList();
};

const onSelectedPage = (pageNo) => {
  currentPage.value = pageNo;
  retrieveList();
}

// 페이지 사이즈 변경.
const onSelectPageSize = (option) => {
  pageSize.value = option.value;
  currentPage.value = 1;
  retrieveList();
};

const show = (selectedLectureItems = []) => {
  baseModal.value.open();
  normalizeSelectedLectures(selectedLectureItems);
  _searchInput.value = getInitialSearchInput();
  currentPage.value = 1;
  pageSize.value = 10;
  setIsSelectedAllSilently(false);
  retrieveList();
  return new Promise((resolve, _) => {
    resolvePromise.value = resolve;
  });
};

const cancel = () => {
  pageSize.value = 10;
  baseModal.value.close();
  resolvePromise.value(false);

  retrieveList();
};

// 저장 버튼 클릭 시 부모로 데이터 전달
const confirm = () => {
  if (selectedResult.value.length !== 0 || selectedLectures.value.length !== 0) {
    baseModal.value.close();
    emit('submitSelection', {
      selectedResult: selectedResult.value,
      selectedLectures: selectedLectures.value,
    }); // 부모에게 데이터 전달
  } else {
    $alert("수료 강의를 선택해 주세요.");
  }
};

defineExpose({
  show,
})

</script>
<template>
  <Modal ref="baseModal" class="vld-parent">
    <div class="modal-inner xl-size">
      <button class="close" @click="cancel"></button>
      <div class="modal-title">
        <h2>수료 강의 선택</h2>
      </div>
      <div class="modal-body">
        <div>
          <div class="filter-wrap open">
            <p>검색</p>
            <div class="filter">
              <KSASelect :options="lectureGb" class="select" @selectOption="onSelectLectureGb" />
              <KSASelect :options="partnerYn" class="select" @selectOption="onSelectPartnerYn" />
              <KSAInput class="text-box" v-model="_searchInput.searchInput" placeholder="강의명을 입력하세요" />
              <KSAPrimaryButton text="검색" @click="onClickSearch" />
            </div>
          </div>
          <div class="list-top">
            <p>총 <span>{{ $number(totalCount)}}</span>개</p>
            <div>
              <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize" />
            </div>
          </div>
          <div class="table-form">
            <table class="vertical">
              <colgroup>
                <col width="5%" />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <tr>
                <th><KSACheckBox v-model="isSelectedAll" /></th>
                <th>구분</th>
                <th>담당 구분</th>
                <th>강의명</th>
                <th>교육 기간</th>
              </tr>
              <tr v-for="lecture in list">
                <td class="text-center"><KSACheckBox v-model="lecture.checked" /></td>
                <td class="text-center">{{ lecture.lectGb === '01' ? '온라인' : '오프라인' }}</td>
                <td class="text-center">{{ lecture.partnerYn === 'Y' ? '비수도권' : '수도권' }}</td>
                <td class="text-overflow long-overflow">{{ lecture.lectName }}</td>
                <td class="text-center">{{ $yyyymmdd(lecture.lectStartYmd) }} ~ {{ $yyyymmdd(lecture.lectEndYmd) }}</td>
              </tr>
              <tr v-if="list.length === 0">
                <td class="text-center" colspan="5">
                  등록된 강의가 없습니다.
                </td>
              </tr>
            </table>
          </div>
          <Paginate
              v-if="list.length > 0"
              v-model="currentPage"
              :page-count="totalPages"
              :click-handler="onSelectedPage"
          />
        </div>
        <div class="residence-selected">
          <span v-for="(option, index) in selectedResult" :key="`${option}-${index}`">
            {{ option }}<i @click="deleteLecture(index)">삭제</i>
          </span>
        </div>
      </div>
      <small class="btn-reset" @click="reset">초기화</small>
      <div class="modal-footer" style="justify-content: center;">
        <KSASecondaryButton class="gray_button" text="취소" @click="cancel" />
        <KSAPrimaryButton class="ml-xs" text="저장" @click="confirm" />
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
@import "../../../../../assets/scss/theme";
.modal-inner {
  text-align: left;
  .filter {
    display: flex;
    padding: 20px;
    gap: 6px;
    .select {
      width: 260px !important;
      &:first-of-type {
        width: 220px !important;
      }
    }
    .custom-select {
      width: 100%;
    }
  }
  .table-form {
    overflow: auto;
    max-height: 500px;
  }
  .residence-selected {
    border: 1px solid #E4E5F0;
    padding: 20px;
    margin-top: 10px;
  }
  .btn-reset {
    position: relative;
    float: right;
    color: #86889A;
    font-size: 16px;
    margin: 6px 30px 0 0;
    cursor: pointer;
    &:before {
      content: "";
      position: absolute;
      left: -24px;
      top: 50%;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      background: url(/src/assets/images/icon-reset.png) no-repeat center;
      background-size: cover;
    }
  }
  .modal-footer {
    display: block;
    padding: 0 30px;
  }
}
</style>